import { useRef } from "react";
import type { RemoteDevice } from "@/mocks/device";

interface ConnectionPanelProps {
  code: string;
  onCodeChange: (v: string) => void;
  onConnect: () => void;
  connecting: boolean;
  error: string;
  history: RemoteDevice[];
  onReconnect: (device: RemoteDevice) => void;
  onRemoveDevice: (code: string) => void;
  onClearHistory: () => void;
}

export default function ConnectionPanel({
  code,
  onCodeChange,
  onConnect,
  connecting,
  error,
  history,
  onReconnect,
  onRemoveDevice,
  onClearHistory,
}: ConnectionPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const canConnect = code.length === 6 && !connecting;

  return (
    <div className="flex flex-col items-center px-6 pb-8 pt-10">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 text-primary-600">
        <i className="ri-remote-control-line text-4xl" />
      </div>

      <h2 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
        デバイスに接続
      </h2>
      <p className="mt-2 text-center text-sm text-foreground-500">
        デバイスに記載された6桁のコードを入力してください
      </p>

      <div
        className="mt-8 flex cursor-text justify-center gap-2"
        onClick={() => inputRef.current?.focus()}
      >
        {Array.from({ length: 6 }).map((_, i) => {
          const active = i === code.length;
          return (
            <div
              key={i}
              className={`flex h-14 w-11 items-center justify-center rounded-xl border-2 font-mono text-2xl font-semibold transition-colors ${
                active
                  ? "border-primary-500 text-foreground-950"
                  : code[i]
                    ? "border-primary-300 bg-primary-50 text-foreground-950"
                    : "border-background-200 text-foreground-400"
              }`}
            >
              {code[i] || ""}
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        value={code}
        onChange={(e) => onCodeChange(e.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        className="sr-only"
        aria-label="デバイスコード"
      />

      {error && (
        <p className="mt-4 flex items-center gap-1.5 text-sm text-primary-700">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      <button
        type="button"
        onClick={onConnect}
        disabled={!canConnect}
        className={`mt-8 flex h-12 w-full max-w-[280px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-all ${
          canConnect
            ? "bg-primary-500 text-background-50 hover:bg-primary-600"
            : "cursor-not-allowed bg-background-200 text-foreground-400"
        }`}
      >
        {connecting ? (
          <>
            <i className="ri-loader-4-line animate-spin" /> 接続中...
          </>
        ) : (
          "接続"
        )}
      </button>

      <p className="mt-6 text-center text-xs text-foreground-400">
        デモコード：774219 · 552810 · 930144
      </p>

      {history.length > 0 && (
        <div className="mt-8 w-full max-w-[280px]">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-500">接続履歴</span>
            <button
              type="button"
              onClick={onClearHistory}
              className="cursor-pointer whitespace-nowrap text-xs text-foreground-400 transition-colors hover:text-foreground-600"
            >
              全て削除
            </button>
          </div>

          <div className="flex flex-col gap-2">
            {history.map((d) => (
              <div
                key={d.code}
                className="flex items-center gap-1 rounded-xl border border-background-200 bg-background-100/60"
              >
                <button
                  type="button"
                  onClick={() => onReconnect(d)}
                  className="flex flex-1 cursor-pointer items-center gap-3 rounded-l-xl px-3 py-2.5 text-left transition-colors hover:bg-background-200/60"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-device-line" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-900">
                      {d.name}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">
                      {d.model} · {d.code}
                    </span>
                  </span>
                  <i className="ri-refresh-line shrink-0 text-foreground-400" />
                </button>
                <button
                  type="button"
                  onClick={() => onRemoveDevice(d.code)}
                  aria-label={`${d.name} を履歴から削除`}
                  className="mr-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-foreground-600"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}